import { useEffect, useRef, useState, type ReactNode } from "react";

interface RevealProps {
  children: ReactNode;
  /** Stagger within a group, in milliseconds. */
  delay?: number;
  as?: "div" | "section" | "li" | "article";
  className?: string;
  id?: string;
}

/**
 * Fades content up as it enters the viewport, once.
 *
 * Uses IntersectionObserver rather than scroll handlers so it costs nothing
 * while idle. Anyone with reduced-motion preferences gets the content
 * immediately - the CSS neutralises the transform and the observer still runs,
 * so nothing is ever left invisible.
 */
export function Reveal({
  children,
  delay = 0,
  as = "div",
  className = "",
  id,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Without observer support, show the content rather than hiding it forever.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const Tag = as;
  return (
    <Tag
      ref={ref as never}
      id={id}
      className={`reveal ${visible ? "reveal--visible" : ""} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}
